import React, { useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { TextField } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { mapBy, uniq, flatten, sortBy } from '../utils/utils';
import { HIDDEN_FEATURES } from '../constants';
import distributorsMap from '../maps/pt';

import Error404Page from './404';

import rootLines from '../assets/images/graphic_elements/roots-lines.png';

import PageLayout from '../components/PageLayout';
import SearchBox from '../components/SearchBox';
import ContactBlock, { Contact } from '../components/ContactBlock';

import * as filterStyles from '../components/FilterBox.module.scss';
import * as styles from './distributors.module.scss';
import { useUpdateUrlFromFilters, useOnLoadEffect } from '../utils/hooks';

import { SVGMap } from '../components/SVGMap';
import SEO from '../components/SEO';

type ID = string;

interface Delegate {
  _id: ID;
  contact: Contact;
  regions: Array<{
    _id: ID;
  }>;
}

interface Company {
  _id: ID;
  contact: Contact;
  location: {
    _id: ID;
  };
}

interface Region {
  _id: ID;
  name: string;
  slug: {
    current: string;
  };
}

interface Location {
  _id: ID;
  name: string;
  region: {
    _id: ID;
  };
  slug: {
    current: string;
  };
}

interface QueryData {
  allSanityDistributorDelegate: {
    nodes: Array<Delegate>;
  };
  allSanityDistributorCompany: {
    nodes: Array<Company>;
  };
  allSanityDistributorRegion: {
    nodes: Array<Region>;
  };
  allSanityDistributorLocation: {
    nodes: Array<Location>;
  };
}

const DistributorsPage = (): React.ReactElement => {
  if (HIDDEN_FEATURES.includes('distributors_page')) {
    return <Error404Page />;
  }

  const { t } = useTranslation();
  const data: QueryData = useStaticQuery(graphql`
    {
      allSanityDistributorDelegate {
        nodes {
          _id
          contact {
            ...Contact
          }
          regions {
            _id
          }
        }
      }
      allSanityDistributorCompany {
        nodes {
          _id
          contact {
            ...Contact
          }
          location {
            _id
          }
        }
      }
      allSanityDistributorRegion {
        nodes {
          _id
          name
          slug {
            current
          }
        }
      }
      allSanityDistributorLocation {
        nodes {
          _id
          name
          region {
            _id
          }
          slug {
            current
          }
        }
      }
    }
  `);

  const {
    allSanityDistributorDelegate: { nodes: allDelegates },
    allSanityDistributorCompany: { nodes: allCompanies },
    allSanityDistributorRegion: { nodes: allRegions },
    allSanityDistributorLocation: { nodes: allLocations },
  } = data;

  const regionsById = mapBy(allRegions, '_id');
  const locationsById = mapBy(allLocations, '_id');

  const usedRegions = sortBy(
    uniq([
      ...allLocations.map(location => location.region._id),
      ...flatten(allDelegates.map(delegate => delegate.regions.map(region => region._id))),
    ]).map(regionId => regionsById[regionId] as Region),
    'name',
  );
  const usedLocations = sortBy(
    uniq(allCompanies.map(company => company.location._id)).map(
      locationId => locationsById[locationId] as Location,
    ),
    'name',
  );

  const regionsBySlug = mapBy(usedRegions, region => region.slug.current);
  const locationsBySlug = mapBy(usedLocations, location => location.slug.current);

  const firstRegion = usedRegions.length > 0 ? usedRegions[0] : null;
  const firstRegionLocation = firstRegion
    ? usedLocations.find(location => location.region._id === firstRegion._id)
    : null;

  const [regionFilter, setRegionFilter] = useState<string | null>(null);
  const [locationFilter, setLocationFilter] = useState<string | null>(null);

  const locationsForFilter = usedLocations.filter(location => location.region._id === regionFilter);

  const filteredDelegates = regionFilter
    ? allDelegates.filter(delegate => delegate.regions.find(region => region._id === regionFilter))
    : [];

  const filteredCompanies = locationFilter
    ? allCompanies.filter(company => company.location._id === locationFilter)
    : [];

  // Get initial filters state from url
  const hasRunOnLoadEffect = useOnLoadEffect(() => {
    let initialRegion = null;
    let initialLocation = null;

    const currentUrl = new URLSearchParams(window.location.search);
    const urlRegionSlugEncoded = currentUrl.get('region');
    const urlLocationSlugEncoded = currentUrl.get('location');
    let urlRegion = urlRegionSlugEncoded
      ? regionsBySlug[decodeURIComponent(urlRegionSlugEncoded)]
      : undefined;
    let urlLocation = urlLocationSlugEncoded
      ? locationsBySlug[decodeURIComponent(urlLocationSlugEncoded)]
      : undefined;

    if (!urlRegion && urlLocation) {
      urlRegion = regionsById[urlLocation.region._id];
    }

    if (urlRegion && (!urlLocation || (urlLocation && urlRegion._id !== urlLocation.region._id))) {
      const urlRegionId = urlRegion._id;
      urlLocation = usedLocations.find(location => location.region._id === urlRegionId);
    }

    initialRegion = urlRegion;
    initialLocation = urlLocation;

    if (!initialRegion && !initialLocation) {
      initialRegion = firstRegion || undefined;
      initialLocation = firstRegionLocation || undefined;
    }

    setRegionFilter(initialRegion ? initialRegion._id : null);
    setLocationFilter(initialLocation ? initialLocation._id : null);
  });

  // Update url on filters state change
  useUpdateUrlFromFilters(() => {
    const region = regionFilter ? regionsById[regionFilter] : null;
    const regionSlugEncoded = region ? encodeURIComponent(region.slug.current) : null;
    const location = locationFilter ? locationsById[locationFilter] : null;
    const locationSlugEncoded = location ? encodeURIComponent(location.slug.current) : null;

    return [['region', regionSlugEncoded] as const, ['location', locationSlugEncoded] as const];
  }, [regionFilter, locationFilter]);

  return (
    <PageLayout
      withPageContentBox
      hasFilters
      title={t('distributors_page.title', 'Distributors')}
      sideBarChildren={
        <div className={filterStyles.box}>
          <div className={filterStyles.group}>
            <span className={filterStyles.blueBordersLabel}>
              {t('distributors_page.filters.title', 'Filter By Geography')}
            </span>
            {hasRunOnLoadEffect && (
              <div className={filterStyles.container}>
                <Autocomplete
                  className={
                    filterStyles.input + (regionFilter ? ' ' + filterStyles.inputSelected : '')
                  }
                  classes={{
                    listbox: filterStyles.selectListbox,
                    groupUl: filterStyles.selectGroupUl,
                    option: filterStyles.selectOption,
                  }}
                  disableClearable
                  disabled={usedRegions.length === 0}
                  options={usedRegions.map(region => region._id)}
                  getOptionLabel={regionId => {
                    if (!regionId) {
                      return '';
                    }
                    return (regionsById[regionId] as Region).name;
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={t('distributors_page.filters.region_label', 'Region')}
                    />
                  )}
                  value={regionFilter || ''}
                  onChange={(_, value) => {
                    setRegionFilter(value);
                    const firstRegionLocation = usedLocations.find(
                      location => location.region._id === value,
                    );
                    setLocationFilter(firstRegionLocation ? firstRegionLocation._id : null);
                  }}
                />

                {usedLocations.length > 0 && locationsForFilter.length !== 0 && (
                  <Autocomplete
                    className={
                      filterStyles.input + (locationFilter ? ' ' + filterStyles.inputSelected : '')
                    }
                    classes={{
                      listbox: filterStyles.selectListbox,
                      groupUl: filterStyles.selectGroupUl,
                      option: filterStyles.selectOption,
                    }}
                    disableClearable
                    options={locationsForFilter.map(location => location._id)}
                    getOptionLabel={locationId => {
                      if (!locationId) {
                        return '';
                      }
                      return (locationsById[locationId] as Location).name;
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={t('distributors_page.filters.location_label', 'Location')}
                      />
                    )}
                    value={locationFilter || ''}
                    onChange={(_, value) => setLocationFilter(value)}
                  />
                )}
              </div>
            )}
          </div>
          {distributorsMap && hasRunOnLoadEffect && (
            <div className={filterStyles.group}>
              <span className={filterStyles.blueBordersLabel}>
                {t('distributors_page.filters.map_title', 'Filter By Map Area')}
              </span>
              <div className={filterStyles.container}>
                <SVGMap
                  map={distributorsMap}
                  className={styles.fullMap}
                  locationClassName={mapLocation =>
                    styles.region +
                    (!regionsBySlug[mapLocation.id] ? ' ' + styles.regionDisabled : '')
                  }
                  getIsLocationSelected={mapLocation => {
                    const mapRegionSlug = mapLocation.id;
                    const currentRegion = regionFilter
                      ? (regionsById[regionFilter] as Region)
                      : null;
                    return !!currentRegion && currentRegion.slug.current === mapRegionSlug;
                  }}
                  onLocationClick={mapLocation => {
                    const mapRegionSlug = mapLocation.id;
                    const region = regionsBySlug[mapRegionSlug];
                    const regionId = region ? region._id : null;
                    if (regionId) {
                      setRegionFilter(regionId);
                      const firstRegionLocation = usedLocations.find(
                        location => location.region._id === regionId,
                      );
                      setLocationFilter(firstRegionLocation ? firstRegionLocation._id : null);
                    }
                  }}
                  childrenBefore={distributorsMap.childrenBefore}
                  childrenAfter={distributorsMap.childrenAfter}
                />
              </div>
            </div>
          )}
        </div>
      }
      searchBox={
        <SearchBox<never, Region | Location>
          contentType="distributor"
          entriesGroups={[
            {
              entries: sortBy(usedRegions, 'name').map(region => ({
                name: region.name,
                url: '/distributors/?region=' + region.slug.current,
                value: region,
              })),
              title: 'Regions',
            },
            {
              entries: sortBy(usedLocations, 'name').map(location => ({
                name: location.name,
                url: '/distributors/?location=' + location.slug.current,
                value: location,
              })),
              title: 'Locations',
            },
          ]}
          noResultsMessage={t(
            'distributors_page.search_box.no_search_results_text',
            'No distributor regions or locations match the searched keyword.',
          )}
          onResultChosen={value => {
            if ('region' in value) {
              setLocationFilter(value._id);
              setRegionFilter(value.region._id);
            } else {
              setRegionFilter(value._id);
              const firstRegionLocation = usedLocations.find(
                location => location.region._id === value._id,
              );
              setLocationFilter(firstRegionLocation ? firstRegionLocation._id : null);
            }
          }}
        ></SearchBox>
      }
    >
      <SEO title={t('distributors_page.title', 'Distributors')} urlPath="/distributors" />
      {hasRunOnLoadEffect && (
        <div>
          <div className={styles.filterIndicationContainer}>
            <div className={styles.infoBlockContainer}>
              {regionFilter && (
                <div className={styles.infoBlock}>
                  <span className={styles.label}>
                    {t('distributors_page.region_label', 'Region')}
                  </span>
                  {/* Default Region */}
                  <span className={styles.info}>{(regionsById[regionFilter] as Region).name}</span>
                </div>
              )}
              {locationFilter && (
                <div className={styles.infoBlock}>
                  <span className={styles.label}>
                    {t('distributors_page.location_label', 'Location')}
                  </span>
                  {/* Default Location */}
                  <span className={styles.info}>
                    {(locationsById[locationFilter] as Location).name}
                  </span>
                </div>
              )}
            </div>
            <span className={styles.infoDescription}>
              <FontAwesomeIcon icon="info-circle" className={styles.infoIcon} />
              {t(
                'distributors_page.mobile_filters_info',
                'You may change Region and Location on Filter',
              )}
            </span>
          </div>
          {filteredDelegates.length > 0 && (
            <section className={styles.delegatesSection}>
              <h2>{t('distributors_page.delegates_label', 'Delegates')}</h2>
              <div className={styles.contacts}>
                {filteredDelegates.map(({ contact }, i) => (
                  <div key={i} className={styles.contactWrapper}>
                    <ContactBlock key={i} contact={contact}></ContactBlock>
                  </div>
                ))}
              </div>
            </section>
          )}
          {filteredDelegates.length > 0 && filteredCompanies.length > 0 && (
            <div className={styles.rootLinesContainer}>
              <img src={rootLines} alt="" className={styles.rootLines} />
            </div>
          )}
          {filteredCompanies.length > 0 && (
            <section className={styles.companiesSection}>
              <h2>{t('distributors_page.companies_label', 'Companies')}</h2>
              <div className={styles.contacts}>
                {filteredCompanies.map(({ contact }, i) => (
                  <div key={i} className={styles.contactWrapper}>
                    <ContactBlock
                      key={i}
                      // Hide images for companies because they're usually crap
                      contact={{ ...contact, image: undefined }}
                      isCompany
                    ></ContactBlock>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </PageLayout>
  );
};

export default DistributorsPage;
